import { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

/**
 * Refuse a route whose id segment is not a positive integer.
 *
 * `/reports/custom/abc` used to reach the page, where `Number(id)` produced
 * `NaN` and the page cheerfully asked api-v1 for
 * `custom_report_definitions/NaN/` — a round-trip, a 4xx, and an error toast,
 * to answer a question the URL had already answered.
 *
 * It sits at the ROUTE rather than inside the page on purpose. Guarding inside
 * the page means an early `return` above that page's hooks, which is exactly
 * the conditional-hook bug this same spec fixed in `generic-report-table`.
 * Here the only hook is `useParams`, and it runs on every render either way.
 */
export default function NumericParamGate({
    param,
    children,
}: {
    param: string;
    children: ReactNode;
}) {
    const params = useParams();
    const raw = params[param];
    const value = Number(raw);

    // `Number('')` and `Number(' ')` are 0, and `Number('1.5')` is not an id —
    // so test the parsed value, not just NaN.
    const valid = raw !== undefined && raw.trim() !== '' && Number.isInteger(value) && value > 0;

    if (!valid) {
        return <Navigate to="/not-found" replace />;
    }

    return <>{children}</>;
}
